import {
  useGetSensorValuesMutation,
  usePostAnnotationMutation,
} from "features/api/apiSlice";
import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import moment from "moment";
import {
  Alert,
  Button,
  Checkbox,
  FormControl,
  Grid,
  InputLabel,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select,
  SelectChangeEvent,
  Snackbar,
  Stack,
  Typography,
} from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers";
import TimeSeriesChart, {
  TimeSeriesAnnotation,
  TimeSeriesValue,
} from "components/charts/TimeSeriesChart";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { IGetSensorValueRequest } from "models/api/requests/sensorvalues/IGetSensorValueRequest";
import { PlusOutlined, ReloadOutlined } from "@ant-design/icons";
import React from "react";
import AddAnnotationDialog from "components/dialogs/Annotations/AddAnnotationDialog";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from 'dayjs';
import { useTranslation } from "react-i18next";
import { IAnnotation } from "models/IAnnotation";
import { IDeviceData, IUser, UserProviderId } from "models/IUser";
import { getSensorTypeName } from "helpers/ExternalDeviceHelper";
import { IExternalDevice } from "models/IExternalDevice";
import { useGetSensorTypesQuery } from "features/api/apiSlice";
import MainCard from "components/MainCard";

type RangeDate = {
  from: Date;
  to: Date;
};

interface Props {
  patientId: string,
  users: IUser[],
  externalDevices: IExternalDevice[],
  annotations: IAnnotation[]
}

export function PatientChartTab({ patientId, users, externalDevices, annotations = [] }: Props) {
  const { t } = useTranslation();
  const user = users!!.find(x => x.id == patientId)!!
  const { data: sensorTypes = [] } = useGetSensorTypesQuery();

  const [rangeDates, setRangeDates] = useState({
    from: moment().startOf("day").subtract(1, "days").toDate(),
    to: moment().endOf("day").toDate()
  } as RangeDate);
  const [searchParams] = useSearchParams();
  const [timeSeriesValues, setTimeSeriesValues] = useState<
    TimeSeriesValue[] | undefined
  >([]);
  const [timeSeriesAnnotations, setTimeSeriesAnnotations] = useState<
    TimeSeriesAnnotation[] | undefined
  >([]);
  const [getSensorValues, { isLoading: isGetSensorValuesLoading }] =
    useGetSensorValuesMutation();
  const [selectedSensors, setSelectedSensors] = useState<string[]>([]);
  const [sensors, setSensors] = useState<string[]>([]);

  const generateTelemetries = (data : IDeviceData[]) => {
    const telemetries: { [key: string]: string } = {};
    
    data.forEach(device => {
        const deviceName = externalDevices.find(extDevice => extDevice.id === device.id)?.name || `Unknown`;

        device.sensorsData.forEach(sensor => {            
            telemetries[`[${deviceName}] ${getSensorTypeName(sensor.typeId, sensorTypes, t)}`] = `${device.id}:${sensor.typeId}`;
        });
    });
    
    return telemetries;
  };

  const telemetriesMap = user.devicesData ? generateTelemetries(user.devicesData!!) : {};

  const allSensorOptions = useMemo(() => {
    return Object.entries(telemetriesMap).map(([label, value]) => ({
      value,
      label,
    }));
  }, [telemetriesMap]);

  useEffect(() => {
    const telemetriesQuery = searchParams.get("telemetries");
    if (telemetriesQuery) {
      const telemetriesParts = telemetriesQuery.split(";");
      const sensorsArray: string[] = telemetriesParts.map((telemetry) => {
        return `${user.id}:${telemetry}`;
      });
      setSensors(sensorsArray);
      setSelectedSensors(telemetriesParts);
    } else {
      setSensors([]);
      setSelectedSensors([]);
    }
    const fromQuery = searchParams.get("from");
    const toQuery = searchParams.get("to");
    setRangeDates({
      from:
        fromQuery && toQuery
          ? moment(fromQuery, "YYYY-MM-DDTHH:mm:ssZ").toDate()
          : moment().startOf("day").subtract(1, "days").toDate(),
      to:
        fromQuery && toQuery
          ? moment(toQuery, "YYYY-MM-DDTHH:mm:ssZ").toDate()
          : moment().endOf("day").toDate()
    });
  }, [user.id, searchParams]);

  const [isChartDataLoading, setIsChartDataLoading] = useState(false);

  useEffect(() => {
    let isCancelled = false;

    async function loadSensorValues() {
      setIsChartDataLoading(true);
      let timeSeriesList: TimeSeriesValue[] = [];

      try {
        if (user.id && sensors.length > 0) {
          for (let sensor of sensors) {
            const request: IGetSensorValueRequest = {
              sensorId: sensor,
              from: rangeDates.from?.toISOString() ?? "",
              to: rangeDates.to?.toISOString() ?? "",
            };
            const response = await getSensorValues(request).unwrap();
            if (response.isSuccess) {
              const timeSeries: TimeSeriesValue[] = response.data.map((c) => {
                return {
                  id: sensor,
                  name: getName(sensor),
                  date: new Date(c.date),
                  value: c.value,
                  units: getUnit(sensor)
                };
              });
              timeSeriesList = [...timeSeriesList, ...timeSeries];
            }
          }
        }

        if (!isCancelled) {
          setTimeSeriesValues(timeSeriesList);
        }
      } finally {
        if (!isCancelled) {
          setIsChartDataLoading(false);
        }
      }
    }

    loadSensorValues();

    return () => {
      isCancelled = true;
    };
  }, [user.id, sensors, rangeDates, getSensorValues]);

  useEffect(() => {
    const filteredAnnotations = (annotations ?? []).filter((c) => {
      if (!c.timelineDate) return false;

      const annotationDate = new Date(c.timelineDate).getTime();
      const fromDate = rangeDates.from.getTime();
      const toDate = rangeDates.to.getTime();

      return annotationDate >= fromDate && annotationDate <= toDate;
    });

    if (filteredAnnotations.length > 0 && users) {
      const data = filteredAnnotations.map((c) => {
        return {
          userRole: users!!.find(x => x.id == c.creationUserId)?.role,
          date: new Date(c.timelineDate),
          description: c.text,
        } as TimeSeriesAnnotation;
      });
      setTimeSeriesAnnotations(data);
      return;
    }

    setTimeSeriesAnnotations([]);
  }, [annotations, users, rangeDates]);

  const getName = (sensorId: string) => {
    const sensorIdParts = sensorId.split(":");
    const telemetry = getKeyByValue(telemetriesMap, `${sensorIdParts[1]}:${sensorIdParts[2]}`);
    return `${telemetry}`;
  };

  const getUnit = (sensorId: string): string | undefined => {
    const [, deviceId, typeId] = sensorId.split(":");

    const device = user.devicesData?.find(d => d.id === deviceId);
    if (!device) return undefined;

    const sensor = device.sensorsData.find(s => s.typeId === typeId);
    return sensor?.unit;
  };

  const getKeyByValue = (obj: any, value: string) => {
    let result = "";
    Object.entries(obj).find(([k, v]) => {
      if (v === value) {
        result = k;
        return true;
      }

      return false;
    });
    return result;
  };

  const [showAddAnnotationDialog, setShowAddAnnotationDialog] = useState<boolean>(false);

  const handleOpenAddAnnotationDialog = () => {
    setShowAddAnnotationDialog(true)
  };

  const handleCloseAddAnnotationDialog = () => {
    setTimelineDate(null)
    setShowAddAnnotationDialog(false);
  };

  const [postAnnotation] = usePostAnnotationMutation();
  const handleAddAnnotation = async (timelineDate: string, text: string) => {
    await postAnnotation({userId: user.id!!, timelineDate, text})
      .unwrap()
      .then((payload) => {
        setShowAddAnnotationDialog(false);
      })
      .catch((error) => {
        setSnackbarMessage(t(error.data.literalKey as string, error.data.message as string))
        setShowSnackbar(true)
      })
  };

  const [showSnackbar, setShowSnackbar] = React.useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState<string | null>();

  const handleCloseSnackbar = (event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }

    setShowSnackbar(false);
    setSnackbarMessage(null)
  };

  const [timelineDate, setTimelineDate] = useState<Date|null>(null);
  const handleOpenAddAnnotationDialogWithTimelineDate = (timelineDate: Date) => {
    setTimelineDate(timelineDate)
    setShowAddAnnotationDialog(true)
  };

  const [tempFrom, setTempFrom] = useState(dayjs(rangeDates.from));
  const [tempTo, setTempTo] = useState(dayjs(rangeDates.to));

  const isLoadDataDisabled = selectedSensors.length === 0 || !tempFrom.isValid() || !tempTo.isValid();

  return (
    <>
    <MainCard>
      <Grid
        container
        sx={{ width: "100%" }}
        spacing={{ xs: 0, md: 2 }}
        columns={{ xs: 12, sm: 12, md: 12 }}
      >
        <Grid container item wrap="nowrap" spacing={2}>
          <Grid item container wrap="nowrap" xs={true}>
            <Button
              variant="contained"
              startIcon={<ReloadOutlined />}
              onClick={() => {
                const from = moment(tempFrom.toDate());
                const to = moment(tempTo.toDate());
                if (from.isValid() && to.isValid()) {
                  setRangeDates({ from: from.toDate(), to: to.toDate() });
                  setSensors(
                    selectedSensors.map((telemetry) => `${user.id}:${telemetry}`)
                  );
                }
              }}
              disabled={isLoadDataDisabled}
              sx={{ textTransform: 'none', marginRight: "15px"}}
            >
              {t('load_data', 'Load data')}
            </Button>
            <FormControl sx={{ minWidth: "250px", marginRight: "15px" }}>
              <InputLabel id="sensors-unified-label" shrink={selectedSensors.length > 0}>{t('sensors', 'Sensors')}</InputLabel>
              <Select
                id="sensors-unified-select"
                multiple
                displayEmpty
                value={selectedSensors}
                onChange={(event: SelectChangeEvent<string[]>) => {
                  setSelectedSensors(event.target.value as string[]);
                }}
                MenuProps={{
                  MenuListProps: {
                    id: 'telemetries-selector-itemlist'
                  },
                  PaperProps: {
                    sx: {
                      '& #telemetries-selector-itemlist .Mui-selected': {
                        backgroundColor: '#4F4E4D !important',
                        color: 'white'
                      }
                    }
                  }
                }}
                input={<OutlinedInput id="select-sensors-unified" />}
                renderValue={(selected) => {
                  return <Typography>{selected.length === 0 ? '' : `${selected.length} ${t('sensors_selected', 'sensor(s) selected')}`}</Typography>;
                }}
                size="small"
                sx={{
                  backgroundColor: 'background.paper',
                  borderRadius: 1,
                  borderColor: 'grey.400',
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'divider',
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'primary.light',
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'primary.main',
                  },
                  height: "41.13px"
                }}
              >
                {allSensorOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    <Checkbox checked={selectedSensors.indexOf(option.value) > -1} color="primary" />
                    <ListItemText primary={option.label} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <LocalizationProvider dateAdapter={AdapterDayjs} localeText={{ start: "Start", end: "Desktop end" }} adapterLocale="es">
              <Stack
                direction="row"
                justifyContent="center"
                alignItems="center"
                spacing={2}
                sx={{ marginRight: "15px" }}
              >
                <DateTimePicker
                  label={t('from', 'From')}
                  value={tempFrom}
                  onChange={(newValue) => {
                    if (newValue) setTempFrom(newValue);
                  }}
                  views={['year', 'month', 'day', 'hours', 'minutes', 'seconds']}
                  format="DD/MM/YYYY HH:mm:ss"
                  slotProps={{
                    textField: {
                      inputProps: {
                        readOnly: true,
                        onPaste: (e: React.ClipboardEvent<HTMLInputElement>) => e.preventDefault(),
                        style: {
                          userSelect: 'none',
                        },
                        onMouseDown: (e) => e.preventDefault()
                      }
                    },
                    actionBar: { actions: [] }
                  }}
                />
                <DateTimePicker
                  label={t('to', 'To')}
                  value={tempTo}
                  onChange={(newValue) => {
                    if (newValue) setTempTo(newValue);
                  }}
                  views={['year', 'month', 'day', 'hours', 'minutes', 'seconds']}
                  format="DD/MM/YYYY HH:mm:ss"
                  slotProps={{
                    textField: {
                      inputProps: {
                        readOnly: true,
                        onPaste: (e: React.ClipboardEvent<HTMLInputElement>) => e.preventDefault(),
                        style: {
                          userSelect: 'none',
                        },
                        onMouseDown: (e) => e.preventDefault()
                      }
                    },
                    actionBar: { actions: [] }
                  }}
                />
              </Stack>
            </LocalizationProvider>
          </Grid>
          { user.provider !== UserProviderId.SMILE && user.provider !== UserProviderId.VETLLAR && <Grid item container alignItems="center" justifyContent='flex-end' xs="auto">
            <Button variant="contained" startIcon={<PlusOutlined />} onClick={handleOpenAddAnnotationDialog} sx={{textTransform: 'none'}}>
              {t('add_annotation', 'Add annotation')}
            </Button>
          </Grid> }
        </Grid>

        <Grid item xs={12} sm={12} md={12}>
          <TimeSeriesChart
            showLoading={isChartDataLoading || isGetSensorValuesLoading}
            values={timeSeriesValues}
            annotations={timeSeriesAnnotations}
            handleOpenAddAnnotationDialogWithTimelineDate={handleOpenAddAnnotationDialogWithTimelineDate}
          />
        </Grid>
      </Grid>

      {showAddAnnotationDialog && <AddAnnotationDialog openDialog={showAddAnnotationDialog} handleClose={handleCloseAddAnnotationDialog} handleAddAnnotation={handleAddAnnotation} timelineDate={timelineDate} />}

      <Snackbar open={showSnackbar} autoHideDuration={6000} onClose={handleCloseSnackbar} anchorOrigin={{ vertical: 'top', horizontal: 'center'}}>
        <Alert onClose={handleCloseSnackbar} severity="error" sx={{ width: '100%' }}>{snackbarMessage}</Alert>
      </Snackbar>
    </MainCard>
    </>
  );
};
